import { useQuery } from '@tanstack/react-query'
import { ingressMonitoringApi, type IngressPeriod } from './api'
import { useAuthStore } from '@/app/store/auth'

type BackendResponse<T> = T | { success: boolean; data: T }

export const ingressKeys = {
    all: ['ingress-monitoring'] as const,
    active: () => [...ingressKeys.all, 'active'] as const,
    history: (period: IngressPeriod) => [...ingressKeys.all, 'history', period] as const,
    summary: (period: IngressPeriod) => [...ingressKeys.all, 'summary', period] as const,
}

export function useIngressActive(refetchInterval: number | false = 10000) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ingressKeys.active(),
        queryFn: async () => {
            const response = await ingressMonitoringApi.getActive()
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: isAuthenticated && !mfaRequired,
        refetchInterval,
        staleTime: 0,
        refetchOnWindowFocus: true,
    })
}

export function useIngressHistory(period: IngressPeriod = 'day') {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ingressKeys.history(period),
        queryFn: async () => {
            const response = await ingressMonitoringApi.getHistory(period)
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useIngressSummary(period: IngressPeriod = 'week') {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: ingressKeys.summary(period),
        queryFn: async () => {
            const response = await ingressMonitoringApi.getSummary(period)
            const backendData = response.data as BackendResponse<any>
            return (backendData as any)?.data || backendData
        },
        enabled: isAuthenticated && !mfaRequired,
    })
}
